import { Box, Stack, Link } from '@packages/shared';
import type { FragmentType } from '@packages/gql/generated/shopping';
import { graphql, getFragmentData } from '@packages/gql/generated/shopping';

import { useIsBot } from '@packages/tracking';
import { useRouter } from 'next/router';
import { getVariationFormattedLink } from '../../../helpers/getVariationFormattedLink';
import { getIsAvailable } from '../../../helpers/getIsAvailable';
import type { SetSelectedVariation } from '../../../types';
import { TileButton } from './TileButton';
import { getStyle } from './getStyle';

const TilesValuesFragment = graphql(/* GraphQL */ `
  fragment TilesValuesFragment on ProductDimension {
    values {
      displayText
      isSelected
      sku
      availability {
        state
      }
    }
  }
`);

export type TilesDimensionProps = {
  /**
   * The GraphQL dimension data for the given product
   */
  dimensionData: FragmentType<typeof TilesValuesFragment>;
  onChange: SetSelectedVariation;
  selectedVariation: string | null;
};

/**
 * TilesDimension to choose a product variation on Detailview
 * */
export const TilesDimension = ({
  dimensionData,
  onChange,
  selectedVariation,
}: TilesDimensionProps) => {
  const dimension = getFragmentData(TilesValuesFragment, dimensionData);
  const router = useRouter();
  const isBot = useIsBot();

  return (
    <Box sx={{ mt: 0.5 }}>
      <Stack
        direction="row"
        useFlexGap
        flexWrap="wrap"
        gap={1.5}
        sx={{ mt: 0.75, button: { position: 'relative' } }}
      >
        {dimension.values.map(({ sku, displayText, availability, isSelected }) => {
          if (!sku) {
            return null;
          }

          return (
            <TileButton
              key={`tilebtn${sku}`}
              sku={sku}
              active={!!isSelected}
              sx={getStyle(getIsAvailable(availability), !!isSelected)}
              size="medium"
              onClick={onChange}
              LinkComponent={Link}
              href={isBot ? getVariationFormattedLink(sku, router) : undefined}
              selectedVariation={selectedVariation}
            >
              {displayText}
            </TileButton>
          );
        })}
      </Stack>
    </Box>
  );
};
